import { getTotalPrice } from '@shared/lib/money';
import i18n from 'i18next';

import {
	CheckinSegmentFragment,
	GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceMeal_Fragment,
	OrderAdditionalServiceGdsServiceServiceType,
	PassengerSelectedGdsServicesProductFragment,
	SegmentFragment
} from '@websky/graphql';
import { SelectedSeatsServiceType, SelectedServiceType } from '../../../Checkout/store/selectedServices/reducers';
import { ServiceOptionItem } from '../../../ServiceSummary/types';

import { SelectedSummaryService, ServiceSummaryType } from '../model/types';

import { getActualProduct } from '../../../ServiceSummary/utils';
import { baggageOptionItemFactory } from './baggageOptionItemFactory';
import { mealOptionItemFactory } from './mealOptionItemFactory';
import { ALL_SEGMENTS_KEY } from '@shared/lib/flight';

export function additionalOptionItemFactory({
	key,
	travellerId,
	segmentsGroupsCount,
	selectedService,
	selectedSegmentsCount,
	gdsServiceRfisc,
	gdsServiceType,
	segments,
	products = [],
	isLoading
}: {
	key: string;
	travellerId: string;
	segmentsGroupsCount: number;
	selectedService: SelectedSummaryService;
	selectedSegmentsCount: number;
	gdsServiceRfisc: string;
	gdsServiceType: ServiceSummaryType;
	segments: (SegmentFragment | CheckinSegmentFragment)[];
	products?: PassengerSelectedGdsServicesProductFragment[];
	isLoading?: boolean;
}): ServiceOptionItem | ServiceOptionItem[] {
	const isAllSegments = key === ALL_SEGMENTS_KEY;

	const serviceId = selectedService.servicePayload.id;
	const gdsService = selectedService.gdsService;

	const count = selectedService.number;
	const confirmedCount = (selectedService.servicePayload as SelectedServiceType)?.confirmedCount;
	const included = confirmedCount !== undefined && count === confirmedCount;

	const segment = !isAllSegments && segments.find(segment => segment.id === key);

	const direction = segment
		? `${segment.departure.airport.iata} – ${segment.arrival.airport.iata}`
		: i18n.t('All flights', { ns: 'Cart' });

	const commonOptionProps = {
		id: serviceId,
		rfisc: gdsServiceRfisc
	};

	switch (gdsServiceType) {
		case 'Seat': {
			const seat = selectedService.servicePayload as SelectedSeatsServiceType;
			const included = !seat.isChangeable && seat.isConfirmed && !seat.onRequest;

			return {
				id: seat.number,
				name: `${direction}, ${seat.number}`,
				segment: key,
				price: seat.seatServices?.length
					? getTotalPrice(seat.seatServices, service => service.price)
					: seat.price,
				status: seat.product?.status,
				deleteMeta: seat.isChangeable
					? {
							serviceId: seat.number,
							passengerId: travellerId,
							segmentId: key,
							type: OrderAdditionalServiceGdsServiceServiceType.Seat,
							allSegments: isAllSegments
					  }
					: null,
				included,
				isLoading
			};
		}
		case 'Baggage':
		case 'Animal':
			const commonServiceObj = {
				key,
				travellerId,
				gdsServiceRfisc,
				count,
				confirmedCount,
				segmentsGroupsCount,
				products,
				isLoading,
				gdsService: gdsService as GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment
			};

			if (confirmedCount && count !== confirmedCount) {
				return [
					baggageOptionItemFactory({ ...commonServiceObj, groupType: 'confirmed' }),
					baggageOptionItemFactory({ ...commonServiceObj, groupType: 'booked' })
				];
			}

			return baggageOptionItemFactory({ ...commonServiceObj, groupType: 'all' });
		case 'Meal': {
			const commonServiceFactoryObj = {
				key,
				serviceId,
				travellerId,
				gdsServiceRfisc,
				direction,
				count,
				confirmedCount,
				products,
				isLoading,
				gdsService: gdsService as GdsService_OrderAdditionalServiceGdsServiceMeal_Fragment
			};

			if (confirmedCount && count !== confirmedCount) {
				return [
					mealOptionItemFactory({ ...commonServiceFactoryObj, groupType: 'confirmed' }),
					mealOptionItemFactory({ ...commonServiceFactoryObj, groupType: 'booked' })
				];
			}

			return mealOptionItemFactory(commonServiceFactoryObj);
		}
		case 'BusinessLounge':
		case 'CapsuleHotel':
		case 'PriorityBoarding': {
			const name =
				gdsService?.name ??
				i18n.t(
					{
						BusinessLounge: 'Business lounge',
						CapsuleHotel: 'Capsule hotel',
						PriorityBoarding: 'Priority boarding'
					}[gdsServiceType],
					{ ns: 'Checkout' }
				);

			return {
				...commonOptionProps,
				segment: key,
				name,
				price: gdsService.price,
				status: getActualProduct(products)?.status,
				deleteMeta: !included
					? {
							serviceId,
							passengerId: travellerId,
							segmentId: key,
							type: OrderAdditionalServiceGdsServiceServiceType.BusinessLounge,
							allSegments: isAllSegments
					  }
					: null,
				included,
				isLoading
			};
		}
		case 'Transfer': {
			const price = { ...gdsService.price };
			price.amount *= segmentsGroupsCount;

			return {
				...commonOptionProps,
				name: gdsService.name,
				segment: key,
				status: getActualProduct(products)?.status,
				deleteMeta: !included
					? {
							serviceId,
							passengerId: travellerId,
							segmentId: key,
							type: OrderAdditionalServiceGdsServiceServiceType.Vip,
							allSegments: isAllSegments
					  }
					: null,
				price,
				included,
				isLoading
			};
		}
		case 'Upgrade': {
			return {
				...commonOptionProps,
				name: direction,
				segment: key,
				price: selectedService.servicePayload.price,
				status: getActualProduct(products)?.status,
				deleteMeta: !included
					? {
							serviceId,
							passengerId: travellerId,
							segmentId: key,
							type: OrderAdditionalServiceGdsServiceServiceType.Vip,
							allSegments: isAllSegments
					  }
					: null,
				included,
				isLoading
			};
		}
		case 'RefundableTicket': {
			return {
				...commonOptionProps,
				name: gdsService.name,
				segment: key,
				included,
				price: selectedService.servicePayload.price,
				status: getActualProduct(products)?.status,
				deleteMeta: null
			};
		}
		case 'Sms': {
			const price = { ...gdsService.price };
			price.amount *= segmentsGroupsCount;

			return {
				...commonOptionProps,
				name: gdsService.name,
				deleteMeta: !included
					? {
							serviceId,
							passengerId: travellerId,
							segmentId: key,
							allSegments: isAllSegments
					  }
					: null,
				price,
				included,
				isLoading
			};
		}
		case 'Misc': {
			const price = { ...gdsService.price };
			price.amount *= selectedSegmentsCount;

			return {
				...commonOptionProps,
				name: gdsService.name,
				segment: key,
				status: getActualProduct(products)?.status,
				deleteMeta: !included
					? {
							serviceId,
							passengerId: travellerId,
							segmentId: key,
							type: OrderAdditionalServiceGdsServiceServiceType.Misc,
							allSegments: isAllSegments
					  }
					: null,
				included,
				price,
				isLoading
			};
		}
		default:
			return null;
	}
}
